import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { useAdminAuth } from './AdminLayout';
import { toast } from 'sonner';
import { 
  Wallet, 
  CreditCard, 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  Eye, 
  Search, 
  Filter, 
  X, 
  TrendingUp, 
  IndianRupee 
} from 'lucide-react';

export default function AdminPayments() {
  const { password } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTxn, setSelectedTxn] = useState(null);
  
  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [gatewayFilter, setGatewayFilter] = useState('all');

  const client = adminApi(password);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, txnsRes] = await Promise.all([
        client.get('/admin/payments/dashboard'),
        client.get('/admin/payments/transactions')
      ]);
      setStats(statsRes.data);
      setTransactions(txnsRes.data);
    } catch (e) {
      toast.error('Failed to load payments data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 12000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  // Filter transactions
  const filteredTxns = transactions.filter((t) => {
    const matchesSearch = 
      t.transaction_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.order_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.customer.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    const matchesGateway = gatewayFilter === 'all' || t.payment_gateway.toLowerCase().includes(gatewayFilter.toLowerCase());
    
    let matchesMethod = true;
    if (methodFilter !== 'all') {
      if (methodFilter === 'cod') {
        matchesMethod = t.payment_method.toLowerCase() === 'cod';
      } else if (methodFilter === 'cashfree') {
        matchesMethod = t.payment_method.toLowerCase() === 'upi / card' || t.payment_method.toLowerCase() === 'cashfree';
      } else if (methodFilter === 'bypassed') {
        matchesMethod = t.payment_method.toLowerCase() === 'bypassed';
      }
    }

    return matchesSearch && matchesStatus && matchesGateway && matchesMethod;
  });

  return (
    <div className="font-body space-y-6">
      <div>
        <h1 className="font-heading font-extrabold text-3xl text-textprimary mb-1">Payments</h1>
        <p className="text-textsecondary text-sm">Monitor revenue, transactions, and payment statuses</p>
      </div>

      {/* Metrics Row */}
      {stats ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3.5">
          {/* Card 1: Total Payments */}
          <div className="bg-white rounded-2xl p-4 shadow-card border border-borderbeige flex flex-col justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-wider font-heading font-bold text-textsecondary block">Total Payments</span>
              <span className="font-heading font-extrabold text-xl text-textprimary mt-1 block">
                {stats.total_payments_count}
              </span>
            </div>
            <div className="text-[10px] font-medium text-textsecondary mt-3 flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5 text-maroon" /> Total Attempted
            </div>
          </div>

          {/* Card 2: Total Revenue */}
          <div className="bg-white rounded-2xl p-4 shadow-card border border-borderbeige flex flex-col justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-wider font-heading font-bold text-textsecondary block">Total Revenue</span>
              <span className="font-heading font-extrabold text-xl text-maroon mt-1 block flex items-center">
                <IndianRupee className="w-4 h-4 inline" />{stats.total_revenue.toFixed(2)}
              </span>
            </div>
            <div className="text-[10px] font-medium text-fresh mt-3">
              ● Successful payments only
            </div>
          </div>

          {/* Card 3: Successful */}
          <div className="bg-white rounded-2xl p-4 shadow-card border border-borderbeige flex flex-col justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-wider font-heading font-bold text-textsecondary block">Successful</span>
              <span className="font-heading font-extrabold text-xl text-fresh mt-1 block">
                {stats.successful_count}
              </span>
            </div>
            <span className="text-[11px] font-heading font-bold text-fresh mt-3 block">
              ₹{stats.successful_amount.toFixed(2)}
            </span>
          </div>

          {/* Card 4: Failed */}
          <div className="bg-white rounded-2xl p-4 shadow-card border border-borderbeige flex flex-col justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-wider font-heading font-bold text-textsecondary block">Failed</span>
              <span className="font-heading font-extrabold text-xl text-red-500 mt-1 block">
                {stats.failed_count}
              </span>
            </div>
            <span className="text-[11px] font-heading font-bold text-red-500 mt-3 block font-body">
              ₹{stats.failed_amount.toFixed(2)}
            </span>
          </div>

          {/* Card 5: Pending */}
          <div className="bg-white rounded-2xl p-4 shadow-card border border-borderbeige flex flex-col justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-wider font-heading font-bold text-textsecondary block">Pending</span>
              <span className="font-heading font-extrabold text-xl text-amber-500 mt-1 block animate-pulse">
                {stats.pending_count}
              </span>
            </div>
            <span className="text-[11px] font-heading font-bold text-amber-500 mt-3 block">
              ₹{stats.pending_amount.toFixed(2)}
            </span>
          </div>
        </div>
      ) : (
        <div className="h-24 bg-bgsecondary animate-pulse rounded-2xl" />
      )}

      {/* Filters & Transaction Table */}
      <div className="bg-white rounded-2xl p-5 shadow-card border border-borderbeige">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-5">
          <h2 className="font-heading font-bold text-lg text-textprimary">Transaction Table</h2>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-textsecondary" />
              <input
                type="text"
                placeholder="Search Txn ID, Order, Customer"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-borderbeige rounded-xl text-xs focus:ring-2 focus:ring-maroon/20 focus:outline-none"
              />
            </div>

            {/* Gateway Filter */}
            <select
              value={gatewayFilter}
              onChange={(e) => setGatewayFilter(e.target.value)}
              className="px-3 py-2 border border-borderbeige rounded-xl text-xs bg-white text-textprimary focus:outline-none"
            >
              <option value="all">All Gateways</option>
              <option value="cashfree">Cashfree</option>
              <option value="cod">COD</option>
              <option value="bypass">Bypass</option>
            </select>

            {/* Method Filter */}
            <select
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value)}
              className="px-3 py-2 border border-borderbeige rounded-xl text-xs bg-white text-textprimary focus:outline-none"
            >
              <option value="all">All Methods</option>
              <option value="cod">COD Only</option>
              <option value="cashfree">Online Only</option>
              <option value="bypassed">Bypassed</option>
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-borderbeige rounded-xl text-xs bg-white text-textprimary focus:outline-none"
            >
              <option value="all">All Statuses</option>
              <option value="Success">Success</option>
              <option value="Pending">Pending</option>
              <option value="Failed">Failed</option>
            </select>
          </div>
        </div>

        {/* Table representation */}
        <div className="overflow-x-auto">
          {loading && transactions.length === 0 ? (
            <div className="text-center py-20 text-textsecondary">Loading transactions...</div>
          ) : filteredTxns.length === 0 ? (
            <div className="text-center py-20 text-textsecondary">No transaction records found matching filters</div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-borderbeige text-textsecondary text-[11px] font-heading font-bold uppercase tracking-wider bg-bgsecondary/30">
                  <th className="py-3 px-4">Transaction ID</th>
                  <th className="py-3 px-4">Order ID</th>
                  <th className="py-3 px-4">Customer</th>
                  <th className="py-3 px-4">Amount</th>
                  <th className="py-3 px-4">Methods</th>
                  <th className="py-3 px-4">Gateway</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4">Payment Date</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borderbeige/60 text-xs">
                {filteredTxns.map((t) => (
                  <tr key={t.transaction_id} className="hover:bg-bgsecondary/10 transition-colors">
                    <td className="py-3.5 px-4 font-mono font-medium text-textprimary max-w-[140px] truncate">
                      {t.transaction_id}
                    </td>
                    <td className="py-3.5 px-4 font-bold text-maroon">
                      #{t.order_id.slice(0, 8).toUpperCase()}
                    </td>
                    <td className="py-3.5 px-4 text-textprimary font-medium">{t.customer}</td>
                    <td className="py-3.5 px-4 font-bold text-textprimary">₹{t.amount.toFixed(2)}</td>
                    <td className="py-3.5 px-4 text-textsecondary">
                      <span className="flex items-center gap-1.5 capitalize">
                        {t.payment_method.toLowerCase().includes('cod') ? (
                          <Wallet className="w-3.5 h-3.5 text-textsecondary" />
                        ) : (
                          <CreditCard className="w-3.5 h-3.5 text-textsecondary" />
                        )}
                        {t.payment_method}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 font-medium text-textsecondary">{t.payment_gateway}</td>
                    <td className="py-3.5 px-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-heading font-extrabold whitespace-nowrap ${
                        t.status === 'Success' ? 'bg-green-50 text-fresh border border-green-200' :
                        t.status === 'Failed' ? 'bg-red-50 text-red-500 border border-red-200' :
                        'bg-amber-50 text-amber-500 border border-amber-200 animate-pulse'
                      }`}>
                        {t.status === 'Success' && <CheckCircle2 className="w-3 h-3" />}
                        {t.status === 'Failed' && <XCircle className="w-3 h-3" />}
                        {t.status === 'Pending' && <AlertCircle className="w-3 h-3" />}
                        {t.status}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-textsecondary">
                      {t.payment_date ? new Date(t.payment_date).toLocaleString() : 'N/A'}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <button
                        onClick={() => setSelectedTxn(t)}
                        className="p-1.5 hover:bg-bgsecondary rounded-lg transition-colors inline-flex items-center text-maroon/80 hover:text-maroon gap-1 text-[11px] font-heading font-bold"
                      >
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Transaction Details Modal */}
      {selectedTxn && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-card border border-borderbeige divide-y divide-borderbeige">
            {/* Header */}
            <div className="flex justify-between items-center pb-4">
              <div>
                <h3 className="font-heading font-extrabold text-lg text-textprimary">Payment Details</h3>
                <span className="text-[10px] text-textsecondary uppercase font-bold tracking-wider">
                  Txn ID: {selectedTxn.transaction_id}
                </span>
              </div>
              <button 
                onClick={() => setSelectedTxn(null)} 
                className="p-1.5 hover:bg-bgsecondary rounded-full transition-colors border border-borderbeige"
              >
                <X className="w-4 h-4 text-textsecondary" />
              </button>
            </div>

            {/* Body Info */}
            <div className="py-4 space-y-3.5 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-textsecondary">Amount Paid</span>
                <span className="font-heading font-extrabold text-lg text-maroon">₹{selectedTxn.amount.toFixed(2)}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-textsecondary">Order Reference</span>
                <span className="font-heading font-bold text-textprimary">#{selectedTxn.order_id.slice(0, 8).toUpperCase()}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-textsecondary">Customer</span>
                <span className="font-medium text-textprimary">{selectedTxn.customer}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-textsecondary">Restaurant/Vendor</span>
                <span className="font-medium text-textprimary">{selectedTxn.restaurant}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-textsecondary">Payment Service</span>
                <span className="font-semibold text-textprimary capitalize">{selectedTxn.payment_gateway}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-textsecondary">Gateway Reference ID</span>
                <span className="font-mono text-textsecondary break-all max-w-[200px] text-right">{selectedTxn.gateway_transaction_id}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-textsecondary">Payment Mode</span>
                <span className="font-medium text-textprimary">{selectedTxn.payment_method}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-textsecondary">Verification Status</span>
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-heading font-extrabold ${
                  selectedTxn.status === 'Success' ? 'bg-green-50 text-fresh border border-green-200' :
                  selectedTxn.status === 'Failed' ? 'bg-red-50 text-red-500 border border-red-200' :
                  'bg-amber-50 text-amber-500 border border-amber-200 animate-pulse'
                }`}>
                  {selectedTxn.status}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-textsecondary">Date Completed</span>
                <span className="text-textsecondary">{selectedTxn.payment_date ? new Date(selectedTxn.payment_date).toLocaleString() : 'N/A'}</span>
              </div>
            </div>

            {/* Footer */}
            <div className="pt-4 text-center">
              <button 
                onClick={() => setSelectedTxn(null)} 
                className="w-full bg-maroon text-white font-heading font-bold py-2.5 rounded-xl hover:bg-maroon-dark transition-colors text-xs"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
